/* eslint-disable @typescript-eslint/naming-convention */ import { DiscordSDK, PermissionUtils, Permissions, RPCCloseCodes } from '@discord/embedded-app-sdk';
import { UIAjaxWrapper } from '../../../../shared/scripts/network/UIAjaxWrapper.ts';
import { UIAPIEndpoints } from '../../../../shared/scripts/network/UIAPIEndpoints.ts';
import { UIUtilsEjsVariables } from '../../../../shared/scripts/UIUtilsEjsVariables.ts';
import axios from 'axios';
import { CA } from '../../../../shared/scripts/analytics/Analytics.ts';
import { AnalyticEventCategory, AnalyticEventType, DiscordChannelTypeEnum } from '../../../../../shared/entity_datas/EntityDatas.ts';
import { COLONIST_VERSION, SharedStrings } from '@colonist/types';
import { StringUtils } from '@colonist/utils';
import { isNil, omitBy } from 'lodash-es';
import { StorageWrapper } from '../../../../shared/scripts/StorageWrapper.ts';
export class SDKInit {
    static async init() {
        const discordSDK = new DiscordSDK(UIUtilsEjsVariables.discordSDKClientId);
        await discordSDK.ready();
        if (!UIUtilsEjsVariables.isUserOnDiscordMobile) {
            try {
                await discordSDK.commands.encourageHardwareAcceleration();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn('encourageHardwareAcceleration failed', error);
            }
        }
        await this.cookiesMustBeAllowedOnSafari(discordSDK);
        const permissionUserProfile = 'identify';
        const permissionEmail = 'email';
        const permissionServerProfile = 'guilds.members.read';
        const permissionGuildInformation = 'guilds';
        const permissionStartActivity = 'rpc.activities.write';
        const permissionCommands = 'applications.commands';
        let code;
        try {
            const authorizeResponse = await discordSDK.commands.authorize({
                client_id: UIUtilsEjsVariables.discordSDKClientId,
                response_type: 'code',
                state: '',
                prompt: 'none',
                scope: [
                    permissionUserProfile,
                    permissionEmail,
                    permissionServerProfile,
                    permissionGuildInformation,
                    permissionStartActivity,
                    permissionCommands
                ]
            });
            code = authorizeResponse.code;
        } catch (err) {
            discordSDK.close(RPCCloseCodes.CLOSE_NORMAL, 'Authorization required');
            throw new Error('Authorization required');
        }
        const data = {
            code: code
        };
        const response = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiGetDiscordAccessToken(), undefined, data);
        if (response == undefined) {
            discordSDK.close(RPCCloseCodes.CLOSE_NORMAL, 'Failed to get discord access token');
            throw new Error('Failed to get discord access token');
        }
        const { accessToken, csrfToken } = response.json;
        UIAjaxWrapper.csrfToken = csrfToken;
        const auth = await discordSDK.commands.authenticate({
            access_token: accessToken
        });
        if (auth == null) {
            discordSDK.close(RPCCloseCodes.CLOSE_ABNORMAL, 'User didn\'t agree to the popup');
            throw new Error('User didn\'t agree to the popup');
        }
        const authResult = await UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.discordActivityAuth(), undefined, {
            accessToken: accessToken
        }, 'application/json');
        if (authResult == undefined) {
            void CA.sendAnalyticEvent({
                category: AnalyticEventCategory.AuthError,
                type: AnalyticEventType.DiscordActivityAuthError
            });
            throw new Error('Failed to authenticate user with Colonist');
        }
        if (authResult.json.accountLinked != undefined && authResult.json.accountLinked == true) {
            StorageWrapper.setDiscordAccountLinked();
        }
        var _discordSDK_guildId;
        const serverProfile = await this.getUserProfile((_discordSDK_guildId = discordSDK.guildId) !== null && _discordSDK_guildId !== void 0 ? _discordSDK_guildId : '', accessToken, discordSDK);
        if (serverProfile == undefined) {
            discordSDK.close(RPCCloseCodes.CLOSE_ABNORMAL, 'Failed to get Discord Server Profile');
            throw new Error('Failed to get Discord Server Profile');
        }
        const { permissions } = await discordSDK.commands.getChannelPermissions();
        const userPermissions = {
            canInviteFriends: PermissionUtils.can(Permissions.CREATE_INSTANT_INVITE, permissions)
        };
        return {
            discordSDK: discordSDK,
            accessToken: accessToken,
            serverProfile: serverProfile,
            userPermissions: userPermissions
        };
    }
    static async getUserProfile(serverId, accessToken, discordSDK) {
        const headers = {
            Authorization: `Bearer ${accessToken}`
        };
        const globalProfileEndpoint = `${this.getDiscordAPIRoot()}/api/users/@me`;
        try {
            const globalUserData = await axios.get(globalProfileEndpoint, {
                headers: headers
            });
            const globalUser = globalUserData.data;
            void this.saveData(serverId, accessToken, globalUser, discordSDK);
            return await this.getGlobalOrServerUserProfile(serverId, accessToken, globalUser);
        } catch (err) {
            let message = err.message;
            if (err.response.status == '429') message = 'You\'ve tried to open the activity too many times. Please try again in a few minutes.';
            discordSDK.close(RPCCloseCodes.CLOSE_ABNORMAL, message);
            return undefined;
        }
    }
    static async getGlobalOrServerUserProfile(serverId, accessToken, globalUser) {
        const isActivityRunningOnDM = serverId == '';
        if (isActivityRunningOnDM) return this.getGlobalUserProfile(globalUser);
        return await this.getServerUserProfile(serverId, accessToken);
    }
    static getGlobalUserProfile(globalUser) {
        var _globalUser_global_name;
        const globalUsername = (_globalUser_global_name = globalUser.global_name) !== null && _globalUser_global_name !== void 0 ? _globalUser_global_name : globalUser.username;
        const profilePictureUrl = this.getGlobalDiscordProfilePictureURL(globalUser);
        return {
            username: StringUtils.removeUnrenderableCharacters(globalUsername),
            profilePictureUrl: profilePictureUrl,
            clientVersion: COLONIST_VERSION
        };
    }
    static async getServerUserProfile(serverId, accessToken) {
        const headers = {
            Authorization: `Bearer ${accessToken}`
        };
        const serverProfileEndpoint = `${this.getDiscordAPIRoot()}/api/users/@me/guilds/${serverId}/member`;
        const serverUserData = await axios.get(serverProfileEndpoint, {
            headers: headers
        });
        const serverUser = serverUserData.data;
        var _serverUser_nick, _ref;
        const serverUsername = (_ref = (_serverUser_nick = serverUser.nick) !== null && _serverUser_nick !== void 0 ? _serverUser_nick : serverUser.user.global_name) !== null && _ref !== void 0 ? _ref : serverUser.user.username;
        const profilePictureUrl = this.getServerDiscordProfilePictureURL(serverId, serverUser);
        return {
            username: StringUtils.removeUnrenderableCharacters(serverUsername),
            profilePictureUrl: profilePictureUrl,
            clientVersion: COLONIST_VERSION
        };
    }
    static async saveData(serverId, accessToken, globalUserData, discordSDK) {
        const headers = {
            Authorization: `Bearer ${accessToken}`
        };
        const serverInformationEndpoint = `${this.getDiscordAPIRoot()}/api/users/@me/guilds`;
        try {
            const serverDataResponse = await axios.get(serverInformationEndpoint, {
                headers: headers,
                params: {
                    with_counts: true
                }
            });
            const serverData = serverDataResponse.data;
            const userData = this.getUserData(globalUserData, serverData.length);
            const serversData = this.getServersData(serverId, serverData);
            let channel;
            if (discordSDK.channelId != null) {
                try {
                    channel = await discordSDK.commands.getChannel({
                        channel_id: discordSDK.channelId
                    });
                } catch (err) {
                    channel = undefined;
                }
            }
            const discordData = {
                userData: userData,
                currentServerId: serverId != '' ? serverId : undefined,
                serversData: serversData,
                channelTypeId: SDKInit.getChannelTypeId(channel)
            };
            await UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiSaveDiscordData(), undefined, {
                data: discordData
            }, 'application/json');
        } catch (err) {
            // eslint-disable-next-line no-console
            console.log('data failure', err);
        }
    }
    static getUserData(globalUserData, serverCount) {
        var _globalUserData_email;
        return {
            discordUserId: globalUserData.id,
            username: globalUserData.username,
            locale: globalUserData.locale,
            verified: globalUserData.verified,
            email: (_globalUserData_email = globalUserData.email) !== null && _globalUserData_email !== void 0 ? _globalUserData_email : undefined,
            flags: globalUserData.flags,
            premiumType: globalUserData.premium_type,
            publicFlags: globalUserData.public_flags,
            serverCount: serverCount,
            joinedServers: [],
            ownedServers: []
        };
    }
    static getServersData(serverId, serverData) {
        const serversData = [];
        serverData.forEach((server)=>{
            const shouldSaveThisServer = this.shouldSaveServerData(serverId, serversData.length, server);
            if (!shouldSaveThisServer) return;
            var _server_vanity_url_code;
            let serverObj = {
                discordServerId: server.id,
                name: server.name,
                owner: server.owner || undefined,
                memberCount: server.approximate_member_count,
                presenceCount: server.approximate_presence_count,
                preferredLocale: server.preferred_locale,
                vanityUrl: (_server_vanity_url_code = server.vanity_url_code) !== null && _server_vanity_url_code !== void 0 ? _server_vanity_url_code : undefined
            };
            serverObj = omitBy(serverObj, isNil);
            serversData.push(serverObj);
        });
        return serversData;
    }
    static shouldSaveServerData(currentServerId, currentNumberSaved, server) {
        const MAX_SERVERS_TO_SAVE = 70;
        if (server.id == SharedStrings.DISCORD_SERVER_ID) return true;
        if (currentServerId == server.id) return true;
        if (currentNumberSaved >= MAX_SERVERS_TO_SAVE) return false;
        if (server.approximate_member_count == undefined) return false;
        if (server.approximate_member_count < 100) return false;
        return true;
    }
    static getServerDiscordProfilePictureURL(serverId, serverUser) {
        const hasServerSpecificProfilePicture = serverUser.avatar != null;
        if (hasServerSpecificProfilePicture) {
            return `https://cdn.discordapp.com/guilds/${serverId}/users/${serverUser.user.id}/avatars/${serverUser.avatar}.png?size=256`;
        }
        const hasUserProfileProfilePicture = serverUser.user.avatar != null;
        if (hasUserProfileProfilePicture) {
            return `https://cdn.discordapp.com/avatars/${serverUser.user.id}/${serverUser.user.avatar}.png?size=256`;
        }
        const defaultAvatarSolidColorId = parseInt(serverUser.user.discriminator, 10) % 5;
        const defaultProfilePicture = `https://cdn.discordapp.com/embed/avatars/${defaultAvatarSolidColorId}.png`;
        return defaultProfilePicture;
    }
    static getGlobalDiscordProfilePictureURL(globalUser) {
        const hasUserProfileProfilePicture = globalUser.avatar != null;
        if (hasUserProfileProfilePicture) {
            return `https://cdn.discordapp.com/avatars/${globalUser.id}/${globalUser.avatar}.png?size=256`;
        }
        const defaultAvatarSolidColorId = parseInt(globalUser.discriminator, 10) % 5;
        const defaultProfilePicture = `https://cdn.discordapp.com/embed/avatars/${defaultAvatarSolidColorId}.png`;
        return defaultProfilePicture;
    }
    static async cookiesMustBeAllowedOnSafari(discordSDK) {
        const userAgent = navigator.userAgent.toLowerCase();
        const isDiscordDesktopClient = userAgent.includes('discord');
        const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome');
        if (isDiscordDesktopClient) return;
        if (!isSafari) return;
        const message = 'Cookies are required for this activity. To open this activity on Safari, navigate to Settings > Privacy and deselect \'Prevent Cross-Site Tracking\'. Or use a different browser.';
        try {
            const hasStorageAccess = await document.hasStorageAccess();
            if (hasStorageAccess) return;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Error checking storage access:', e);
            discordSDK.close(RPCCloseCodes.CLOSE_UNSUPPORTED, message);
            return;
        }
        try {
            await document.requestStorageAccess();
        // If no error (promise fulfills), storage access was granted
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Error requesting storage access:', e);
            discordSDK.close(RPCCloseCodes.CLOSE_UNSUPPORTED, message);
        }
    }
    static getChannelTypeId(channel) {
        if (channel == undefined) return undefined;
        if (channel.type in DiscordChannelTypeEnum) return channel.type;
        return DiscordChannelTypeEnum.Unhandled;
    }
    static getDiscordAPIRoot() {
        if (UIUtilsEjsVariables.isUserOnDiscordMobile) return `/.proxy/discord-api`;
        return `https://discord.com`;
    }
}
